/**
 * Tasks — the NEW task drawer's saves (Tasks redesign Phase B2, 2026-10-06).
 *
 * Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md — "Tasks 2a" (Drawer row) and
 * "Phase B — B2". Used by src/pages/TaskList/components/TaskDrawer.vue.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE AND CLOCK-FREE (the node-tests execute it verbatim). `today`
 *    ('YYYY-MM-DD', moment().format in main.js' default zone — the same "day" myDay.js uses),
 *    `now` (a Date) and `timeZone` (GENERAL_TIMEZONE) are always passed in.
 *
 * ── THE SOURCE OF TRUTH ────────────────────────────────────────────────────────────────────────
 * src/pages/TaskList/components/TodoDetails.vue (the old 750px panel, untouched) saves the WHOLE
 * task once, on "Save Changes". The new drawer saves ONE field at a time, as soon as it changes, so
 * every builder below returns a small tasks/updateTask payload holding that field exactly as
 * TodoDetails' save() writes it, plus:
 *   • `id`, `siteID` (when the task has one — updateTask's site-tag sync reads it) and the task's
 *     own `tags` — tasks/updateTask writes `tags: []` when a payload has none (store/tasks.js);
 *   • `dueAtStr` (unchanged) when the task has one — updateTask reads moment(payload.dueAtStr) to
 *     decide whether to reset the notification bell; TodoDetails' save always carries it;
 *   • `lastActivity` = ctx.now, as TodoDetails' save stamps it.
 * tasks/updateTask is updateDoc (a partial update), so the fields a patch leaves out are kept.
 *
 * cypress/node-tests/tasks-drawer-model.test.js runs each builder next to a VERBATIM copy of the
 * TodoDetails / TodoItem / ToDo / store logic it reproduces (SOURCE-guarded) — fix THIS file to
 * match the old code, never the other way round. Four DELIBERATE differences, each because the old
 * full-task save silently fails to persist the change under a partial update (the old code only
 * spreads a field in when it is truthy / non-empty, so a cleared value never reaches Firestore):
 *   1. repeatPatch('none') writes { repeat: false, repeatFrequency: 'Never' } — TodoDetails'
 *      "Never" deletes the keys locally and the save leaves the stored repeat in place.
 *   2. contentPatch always writes taggedPatients (also []), wikiPagesPatch always writes
 *      wikiPages (also []) — TodoDetails omits both when empty, so removing the last one is lost.
 *   3. reopenPatch writes completedAt / completedAtStr / completedByEmpID = null (the store's own
 *      repeating-task revert writes null too); TodoItem / TodoDetails delete them locally only.
 *   4. Repeat options also reset the fields the old form left stale when switching frequency
 *      (excludeSaturday / excludeSunday off unless "Every weekday", repeatOnLastDayOfMonth false).
 * Two refusals the old panel did not have: a whitespace-only title (contentPatch → null) and a
 * blank-only comment (commentPayload → null).
 * The due date a repeat option lands on is computed the way the store getter tasks/getDueDate does
 * (TodoDetails' repeat watchers), in the office zone; TodoDetails' extra moment-default-zone hop
 * (a Yearly date at New York midnight read in a western office) is not copied.
 *
 * Subtasks are NOT saved with tasks/updateTask: TodoDetails autosaves them with
 * tasks/updateTaskSubtasks({ taskId, subtasks }) — subtasksPatch returns that list (plus id/tags).
 */

export const DEFAULT_TIME_ZONE = 'America/New_York';

/** TodoDetails' weekday radio values (weeklyIntervalOptions) — Thursday is 'Thurs'. */
export const WEEKDAY_VALUES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat'];
/** store getDueDate's ISO list (Mon = 1 … Sun = 7). */
const ISO_DAYS = ['Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Both Thursday spellings: TodoDetails stores 'Thurs', moment's 'ddd' (myDay.js) says 'Thu'.
const DAY_BY_VALUE = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Thurs: 4, Fri: 5, Sat: 6 };
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Save to CommLog" is allowed only with exactly this many tagged patients (TodoDetails). */
export const COMMLOG_PATIENTS = 1;

const arr = v => (Array.isArray(v) ? v : []);
const isObj = v => !!v && typeof v === 'object';
const pad2 = n => (n < 10 ? '0' : '') + n;
const present = v => v !== undefined && v !== null && v !== '';

// ---- Days (same algorithms as myDay.js; the node-test checks they agree) ----------------------

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function partsOfYmd(ymd) {
  const m = typeof ymd === 'string' ? YMD_RE.exec(ymd) : null;
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
}

function ymdOfUtc(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** 'YYYY-MM-DD' of a calendar (y, monthIndex 0-11, d) — out-of-range days / months roll. */
function ymdOf(y, mi, d) {
  return ymdOfUtc(Date.UTC(y, mi, d));
}

/** 'YYYY-MM-DD' + n days, or null. */
export function addDaysYmd(ymd, n) {
  const p = partsOfYmd(ymd);
  return p ? ymdOf(p.y, p.m - 1, p.d + (Number(n) || 0)) : null;
}

/** 0 (Sun) … 6 (Sat) of a 'YYYY-MM-DD', or null. */
export function dayIndexOf(ymd) {
  const p = partsOfYmd(ymd);
  return p ? new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay() : null;
}

function daysInMonth(y, mi) {
  return new Date(Date.UTC(y, mi + 1, 0)).getUTCDate();
}

const FORMATTERS = {};
function formatterFor(tz) {
  if (!FORMATTERS[tz]) {
    FORMATTERS[tz] = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }
  return FORMATTERS[tz];
}

function wallClock(ms, tz) {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  let f;
  try {
    f = formatterFor(tz || DEFAULT_TIME_ZONE);
  } catch (e) {
    f = formatterFor(DEFAULT_TIME_ZONE);
  }
  const p = {};
  f.formatToParts(d).forEach((x) => { p[x.type] = x.value; });
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), h: Number(p.hour) % 24, mi: Number(p.minute), s: Number(p.second) };
}

function zoned(ms, tz) {
  const w = wallClock(ms, tz);
  return w ? { ymd: `${w.y}-${pad2(w.m)}-${pad2(w.d)}`, minutes: w.h * 60 + w.mi } : null;
}

function offsetMinutes(ms, tz) {
  const w = wallClock(ms, tz);
  if (!w) return 0;
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s);
  return Math.round((asUtc - (ms - (((ms % 1000) + 1000) % 1000))) / 60000);
}

/** moment.tz(ymd, tz).startOf('day') as epoch ms, or null (DST-midnight days start at their first minute). */
export function startOfDayMs(ymd, tz) {
  const p = partsOfYmd(ymd);
  if (!p) return null;
  const zone = tz || DEFAULT_TIME_ZONE;
  const wallMs = Date.UTC(p.y, p.m - 1, p.d);
  let ms = wallMs - offsetMinutes(wallMs, zone) * 60000;
  ms = wallMs - offsetMinutes(ms, zone) * 60000;
  const z = zoned(ms, zone);
  if (z && z.ymd < ymd) {
    while (zoned(ms, zone).ymd < ymd) ms += 60000 * 15;
    while (zoned(ms - 60000, zone).ymd === ymd) ms -= 60000;
  } else if (z && z.ymd === ymd && z.minutes > 0) {
    ms -= z.minutes * 60000;
    if (zoned(ms, zone).ymd !== ymd) ms += z.minutes * 60000;
  }
  return ms;
}

/** The office day an instant (Date / epoch ms / Firestore Timestamp) falls on, or null. */
export function ymdInZone(value, tz) {
  let ms = null;
  if (value instanceof Date) ms = value.getTime();
  else if (typeof value === 'number') ms = value;
  else if (isObj(value) && typeof value.toDate === 'function') {
    try { ms = value.toDate().getTime(); } catch (e) { ms = null; }
  } else if (isObj(value) && typeof value.seconds === 'number') ms = value.seconds * 1000;
  if (ms === null || !Number.isFinite(ms)) return null;
  const z = zoned(ms, tz || DEFAULT_TIME_ZONE);
  return z ? z.ymd : null;
}

/** TodoDetails' dueAtStr format: 'DD/MM/YYYY' for AFRICA/MASERU offices, else 'MM/DD/YYYY'. */
export function dueAtStrFormat(tz) {
  return String(tz || DEFAULT_TIME_ZONE).toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
}

/** 'YYYY-MM-DD' → 'MM/DD/YYYY' | 'DD/MM/YYYY' ('' when invalid). */
export function formatYmd(ymd, format) {
  const p = partsOfYmd(ymd);
  if (!p) return '';
  return format === 'DD/MM/YYYY' ? `${pad2(p.d)}/${pad2(p.m)}/${p.y}` : `${pad2(p.m)}/${pad2(p.d)}/${p.y}`;
}

/**
 * The day a task is due on, for the drawer: dueAt (Date / Timestamp — what tasks/getTaskDetails
 * returns) in the office zone; else dueAtStr in the office's own format; else a list row's
 * 'MM/DD/YYYY h:mm:ss a' dueAt string. null when there is none.
 */
export function dueDayOf(task, tz) {
  if (!isObj(task)) return null;
  const byInstant = ymdInZone(task.dueAt, tz);
  if (byInstant) return byInstant;
  const parse = (s, dmy) => {
    const m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(s || ''));
    if (!m) return null;
    const mo = Number(dmy ? m[2] : m[1]);
    const da = Number(dmy ? m[1] : m[2]);
    if (mo < 1 || mo > 12 || da < 1 || da > daysInMonth(Number(m[3]), mo - 1)) return null;
    return `${m[3]}-${pad2(mo)}-${pad2(da)}`;
  };
  return parse(task.dueAtStr, dueAtStrFormat(tz) === 'DD/MM/YYYY') || parse(task.dueAt, false);
}

// ---- The common part of every patch ------------------------------------------------------------

/** Copy of a task without the comments array (tasks/getTaskDetails adds it; it must never be written back). */
export function withoutComments(task) {
  const out = {};
  if (!isObj(task)) return out;
  Object.keys(task).forEach((k) => { if (k !== 'comments') out[k] = task[k]; });
  return out;
}

/** { id, siteID?, tags, dueAtStr?, lastActivity? } — see the header. */
export function basePatch(task, ctx) {
  const t = isObj(task) ? task : {};
  const patch = { id: t.id, tags: arr(t.tags).slice() };
  if (present(t.siteID)) patch.siteID = t.siteID;
  if (typeof t.dueAtStr === 'string' && t.dueAtStr !== '') patch.dueAtStr = t.dueAtStr;
  const now = ctx && ctx.now;
  if (now instanceof Date && !Number.isNaN(now.getTime())) patch.lastActivity = new Date(now.getTime());
  return patch;
}

// ---- Title (content) ---------------------------------------------------------------------------

/** src/utils/taskPatients.js getTaggedPatientIDs (copied; agreement-tested): numeric PatNums, in order. */
export function taggedPatientIDs(contentHTML) {
  if (!contentHTML || typeof contentHTML !== 'string') return [];
  const regex = /data-teamio-patientid="([0-9]+)/g;
  const ids = [];
  let m;
  while ((m = regex.exec(contentHTML)) !== null) ids.push(parseInt(m[1], 10));
  return ids;
}

/** TodoDetails' "Please enter a task" check reads innerText: any visible text, badge names included. */
export function hasContentText(contentHTML) {
  if (typeof contentHTML !== 'string') return false;
  const text = contentHTML
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ');
  return text.trim().length > 0;
}

/**
 * The title save — TodoDetails: content = the editor's innerHTML, taggedPatients = its patient
 * badges (written even when [] — difference 2). null when the title has no text (TodoDetails
 * refuses to save: "Please enter a task before saving").
 */
export function contentPatch(task, contentHTML, ctx) {
  if (!hasContentText(contentHTML)) return null;
  return Object.assign(basePatch(task, ctx), {
    content: contentHTML,
    taggedPatients: taggedPatientIDs(contentHTML),
  });
}

// ---- Assigned to -------------------------------------------------------------------------------

/** TodoDetails: only the task's creator may change who it is assigned to. */
export function canEditAssignees(task, userID) {
  return isObj(task) && userID !== undefined && userID !== null && task.createdByEmpID === userID;
}

/** TodoDetails' save: assignees = the picked EmployeeIDs (assignedToEmpID is not touched). */
export function assigneesPatch(task, employeeIDs, ctx) {
  const seen = {};
  const ids = [];
  arr(employeeIDs).forEach((id) => {
    if (!present(id) || seen[String(id)]) return;
    seen[String(id)] = true;
    ids.push(id);
  });
  return Object.assign(basePatch(task, ctx), { assignees: ids });
}

function sameList(a, b) {
  return JSON.stringify(a) === JSON.stringify(b); // mixin.js arraysEqual
}

/**
 * TodoDetails getNewEmployeesToNotify: nothing when the lists are equal; everyone in `after` when
 * nothing is shared; else the newly added ones.
 */
export function newAssignees(before, after) {
  const b = Array.isArray(before) ? before : [];
  const a = Array.isArray(after) ? after : [];
  if (Array.isArray(before) && sameList(before, after)) return [];
  const inBoth = a.filter((id, i) => a.indexOf(id) === i && b.indexOf(id) !== -1);
  if (inBoth.length === 0) return a.slice();
  return a.filter((id, i) => a.indexOf(id) === i && b.indexOf(id) === -1);
}

/**
 * The tasks/addTaskAssignmentNotifications `notifications` for an assignee change — TodoDetails'
 * save, field for field (never yourself; the store fills #taskID#).
 * ctx = { userID, userName, siteID (the logged-in site), taskID, now }.
 */
export function assignmentNotifications(before, after, ctx) {
  const c = isObj(ctx) ? ctx : {};
  return newAssignees(before, after)
    .filter(people => people !== c.userID)
    .map(people => ({
      audience: 'EMPLOYEE',
      employeeID: people,
      type: 'TASK',
      text: `You have been assigned a <a href='/app/todo/#taskID#'>task</a> by ${c.userName}`,
      siteID: c.siteID,
      createdAt: c.now instanceof Date ? new Date(c.now.getTime()) : c.now,
      unreadList: [people],
      taskID: c.taskID,
    }));
}

// ---- Due date ----------------------------------------------------------------------------------

/** TodoDetails' save: a Daily task that excludes Sat / Sun moves forward off those days. */
export function skipExcludedDays(task, ymd) {
  const t = isObj(task) ? task : {};
  if (!t.repeat || t.repeatFrequency !== 'Daily' || !partsOfYmd(ymd)) return ymd;
  const excluded = [];
  if (t.excludeSaturday) excluded.push(6);
  if (t.excludeSunday) excluded.push(0);
  let day = ymd;
  while (excluded.indexOf(dayIndexOf(day)) !== -1) day = addDaysYmd(day, 1);
  return day;
}

/**
 * The due-date save: dueAt = the start of the day in the office zone, dueAtStr = that day in the
 * office format (TodoDetails' save normalisation), after the Daily exclude-days skip. null when
 * `ymd` is not a 'YYYY-MM-DD'. ctx = { timeZone, now }.
 */
export function dueDatePatch(task, ymd, ctx) {
  const tz = (ctx && ctx.timeZone) || DEFAULT_TIME_ZONE;
  if (!partsOfYmd(ymd)) return null;
  const day = skipExcludedDays(task, ymd);
  const ms = startOfDayMs(day, tz);
  if (ms === null) return null;
  return Object.assign(basePatch(task, ctx), { dueAt: new Date(ms), dueAtStr: formatYmd(day, dueAtStrFormat(tz)) });
}

/** The popover's quick picks: Today, Tomorrow, Next Monday (the first Monday after today), In a week. */
export function dueDatePresets(today) {
  if (!partsOfYmd(today)) return [];
  const toMonday = ((8 - dayIndexOf(today)) % 7) || 7;
  return [
    { key: 'today', label: 'Today', ymd: today },
    { key: 'tomorrow', label: 'Tomorrow', ymd: addDaysYmd(today, 1) },
    { key: 'nextMonday', label: 'Next Monday', ymd: addDaysYmd(today, toMonday) },
    { key: 'inAWeek', label: 'In a week', ymd: addDaysYmd(today, 7) },
  ];
}

// ---- Repeat ------------------------------------------------------------------------------------

/**
 * store getter tasks/getDueDate (what TodoDetails' repeat watchers set dueAt to), as a day:
 *   Daily → today; Weekly / Bi-Weekly → the next repeatOn (today if it is today; Bi-Weekly jumps
 *   two weeks when this week's has passed); Monthly → repeatOnDay this month if still ahead, else
 *   next month ('yes' last-day → the end of the due month); Yearly → repeatOnMonth/repeatOnDay this
 *   year, next year when the month has passed; anything else → the current due day.
 * Out-of-range days roll into the next month, as moment's set('date') / Date do.
 */
export function repeatDueDay(task, today, dueDay) {
  const t = isObj(task) ? task : {};
  const p = partsOfYmd(today);
  if (!p) return dueDay || null;
  const iso = dayIndexOf(today) || 7;
  switch (t.repeatFrequency) {
    case 'Daily':
      return today;
    case 'Weekly':
    case 'Bi-Weekly': {
      const need = ISO_DAYS.indexOf(t.repeatOn) + 1;
      if (iso === need) return today;
      if (iso < need) return addDaysYmd(today, need - iso);
      return addDaysYmd(today, (t.repeatFrequency === 'Weekly' ? 7 : 14) + need - iso);
    }
    case 'Monthly': {
      if (t.repeatOnLastDayOfMonth === 'yes') {
        const due = partsOfYmd(dueDay) || p;
        return ymdOf(due.y, due.m - 1, daysInMonth(due.y, due.m - 1));
      }
      const d = p.d;
      const day = Number(t.repeatOnDay);
      if (t.repeatOnDay === d) return today;
      if (!Number.isFinite(day) || !present(t.repeatOnDay)) return dueDay || null;
      if (t.repeatOnDay > d) return ymdOf(p.y, p.m - 1, day);
      return ymdOf(p.y, p.m, day);
    }
    case 'Yearly': {
      const m = p.m - 1;
      if (t.repeatOnMonth && t.repeatOnLastDayOfMonth === 'yes') {
        const month = parseInt(t.repeatOnMonth, 10);
        if (!Number.isFinite(month)) return dueDay || null;
        const last = ymdOf(p.y, month - 1, daysInMonth(p.y, month - 1));
        if (t.repeatOnMonth < m + 1) {
          const q = partsOfYmd(last);
          return ymdOf(q.y + 1, q.m - 1, Math.min(q.d, daysInMonth(q.y + 1, q.m - 1)));
        }
        return last;
      }
      if (t.repeatOnMonth && t.repeatOnDay) {
        const year = t.repeatOnMonth < m + 1 ? p.y + 1 : p.y;
        const mo = Number(t.repeatOnMonth);
        const da = Number(t.repeatOnDay);
        if (!Number.isFinite(mo) || !Number.isFinite(da) || mo < 1 || mo > 12 || da < 1 || da > 31) return dueDay || null;
        return ymdOf(year, mo - 1, da);
      }
      return dueDay || null;
    }
    default:
      return dueDay || null;
  }
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const OFF_DAYS = { excludeSaturday: false, excludeSunday: false };

/**
 * The Repeat menu, built around `anchor` (the task's due day, else today):
 *   none · daily · weekdays (Daily excluding Sat + Sun) · weekly / biweekly on the anchor's weekday
 *   · monthly on its date · yearly on its month + date.
 * `fields` are the TodoDetails fields each one sets (weekday values as its radio stores them).
 */
export function repeatOptions(anchor) {
  const p = partsOfYmd(anchor);
  const opts = [{ key: 'none', label: 'Doesn’t repeat', fields: { repeat: false, repeatFrequency: 'Never' } }];
  opts.push({ key: 'daily', label: 'Every day', fields: Object.assign({ repeat: true, repeatFrequency: 'Daily' }, OFF_DAYS) });
  opts.push({ key: 'weekdays', label: 'Every weekday', fields: { repeat: true, repeatFrequency: 'Daily', excludeSaturday: true, excludeSunday: true } });
  if (!p) return opts;
  const wd = dayIndexOf(anchor);
  const name = DAY_NAMES[wd];
  opts.push({ key: 'weekly', label: `Every ${name}`, fields: Object.assign({ repeat: true, repeatFrequency: 'Weekly', repeatOn: WEEKDAY_VALUES[wd] }, OFF_DAYS) });
  opts.push({ key: 'biweekly', label: `Every other ${name}`, fields: Object.assign({ repeat: true, repeatFrequency: 'Bi-Weekly', repeatOn: WEEKDAY_VALUES[wd] }, OFF_DAYS) });
  opts.push({
    key: 'monthly',
    label: `Monthly on the ${ordinal(p.d)}`,
    fields: Object.assign({ repeat: true, repeatFrequency: 'Monthly', repeatOnDay: p.d, repeatOnLastDayOfMonth: false }, OFF_DAYS),
  });
  opts.push({
    key: 'yearly',
    label: `Yearly on ${MONTHS_SHORT[p.m - 1]} ${p.d}`,
    fields: Object.assign({ repeat: true, repeatFrequency: 'Yearly', repeatOnDay: p.d, repeatOnMonth: p.m, repeatOnLastDayOfMonth: false }, OFF_DAYS),
  });
  return opts;
}

/** Whether the task repeats (TodoDetails / TodoItem show the repeat only with `repeat` AND a frequency). */
export function repeats(task) {
  return isObj(task) && !!task.repeat && present(task.repeatFrequency) && task.repeatFrequency !== 'Never';
}

/** The task's repeat as a short label ("Every Monday", "Every weekday", "Doesn’t repeat" …). */
export function repeatSummary(task) {
  if (!repeats(task)) return 'Doesn’t repeat';
  const t = task;
  const day = DAY_NAMES[DAY_BY_VALUE[t.repeatOn]];
  switch (t.repeatFrequency) {
    case 'Daily':
      if (t.excludeSaturday && t.excludeSunday) return 'Every weekday';
      if (t.excludeSaturday) return 'Every day except Saturday';
      if (t.excludeSunday) return 'Every day except Sunday';
      return 'Every day';
    case 'Weekly':
      return day ? `Every ${day}` : 'Every week';
    case 'Bi-Weekly':
      return day ? `Every other ${day}` : 'Every other week';
    case 'Monthly':
      if (t.repeatOnLastDayOfMonth === 'yes' || t.repeatOnLastDayOfMonth === true) return 'Monthly on the last day';
      return Number(t.repeatOnDay) > 0 ? `Monthly on the ${ordinal(Number(t.repeatOnDay))}` : 'Monthly';
    case 'Yearly': {
      const mo = MONTHS_SHORT[parseInt(t.repeatOnMonth, 10) - 1];
      if (mo && (t.repeatOnLastDayOfMonth === 'yes' || t.repeatOnLastDayOfMonth === true)) return `Yearly on the last day of ${mo}`;
      return mo && Number(t.repeatOnDay) > 0 ? `Yearly on ${mo} ${Number(t.repeatOnDay)}` : 'Yearly';
    }
    default:
      return `Repeats ${String(t.repeatFrequency).toLowerCase()}`;
  }
}

/** The option key the task's current repeat equals, or null (a repeat the menu does not offer). */
export function repeatKeyOf(task, anchor) {
  if (!repeats(task)) return 'none';
  const t = task;
  const hit = repeatOptions(anchor).find((o) => {
    if (o.key === 'none') return false;
    const f = o.fields;
    if (f.repeatFrequency !== t.repeatFrequency) return false;
    if (f.repeatFrequency === 'Daily') return !!t.excludeSaturday === f.excludeSaturday && !!t.excludeSunday === f.excludeSunday;
    if (f.repeatOn !== undefined && DAY_BY_VALUE[t.repeatOn] !== DAY_BY_VALUE[f.repeatOn]) return false;
    if (f.repeatOnDay !== undefined && (Number(t.repeatOnDay) !== f.repeatOnDay || t.repeatOnLastDayOfMonth === 'yes' || t.repeatOnLastDayOfMonth === true)) return false;
    if (f.repeatOnMonth !== undefined && Number(t.repeatOnMonth) !== f.repeatOnMonth) return false;
    return true;
  });
  return hit ? hit.key : null;
}

/**
 * The Repeat save for option `key` (repeatOptions(anchor)): its fields, and — for a repeating
 * option — the due date TodoDetails lands on (repeatDueDay, then the Daily exclude-days skip),
 * office start-of-day. 'none' leaves the due date alone. null for an unknown key.
 * ctx = { today, timeZone, now, anchor? } (anchor defaults to the due day, else today).
 */
export function repeatPatch(task, key, ctx) {
  const c = isObj(ctx) ? ctx : {};
  const tz = c.timeZone || DEFAULT_TIME_ZONE;
  const due = dueDayOf(task, tz);
  const anchor = c.anchor || due || c.today;
  const opt = repeatOptions(anchor).find(o => o.key === key);
  if (!opt) return null;
  const patch = Object.assign(basePatch(task, c), opt.fields);
  if (!opt.fields.repeat) return patch;
  const merged = Object.assign({}, isObj(task) ? task : {}, opt.fields);
  const day = skipExcludedDays(merged, repeatDueDay(merged, c.today, due) || due || c.today);
  const ms = startOfDayMs(day, tz);
  if (ms !== null) {
    patch.dueAt = new Date(ms);
    patch.dueAtStr = formatYmd(day, dueAtStrFormat(tz));
  }
  return patch;
}

// ---- Tags --------------------------------------------------------------------------------------

/** A tag as typed: trimmed, inner whitespace collapsed ('' when nothing is left). */
export function cleanTag(text) {
  return typeof text === 'string' ? text.trim().replace(/\s+/g, ' ') : '';
}

/** The Tags save: the task's tags become `tags` (strings, trimmed, once each). */
export function tagsPatch(task, tags, ctx) {
  const out = [];
  arr(tags).forEach((g) => {
    const v = cleanTag(g);
    if (v && out.indexOf(v) === -1) out.push(v);
  });
  return Object.assign(basePatch(task, ctx), { tags: out });
}

/**
 * The "Add tag" popover for `query`: up to `limit` site tags not on the task that contain it
 * (case-insensitive, A–Z); `create` = the cleaned query when no site tag equals it (case-insensitive)
 * and the task does not have it; `already` = the task has it.
 */
export function tagChoices(siteTags, taskTags, query, limit) {
  const q = cleanTag(query);
  const ql = q.toLowerCase();
  const mine = arr(taskTags).filter(g => typeof g === 'string');
  const mineL = mine.map(g => g.toLowerCase());
  const site = [];
  arr(siteTags).forEach((g) => {
    const v = cleanTag(g);
    if (v && site.indexOf(v) === -1) site.push(v);
  });
  const max = Number(limit) > 0 ? Number(limit) : 8;
  const suggestions = site
    .filter(g => mineL.indexOf(g.toLowerCase()) === -1 && (!ql || g.toLowerCase().indexOf(ql) !== -1))
    .sort((a, b) => a.localeCompare(b))
    .slice(0, max);
  const already = !!ql && mineL.indexOf(ql) !== -1;
  const exists = !!ql && site.some(g => g.toLowerCase() === ql);
  return { suggestions, create: q && !already && !exists ? q : '', already };
}

// ---- Subtasks ----------------------------------------------------------------------------------

/** { done, total } of a subtasks list. */
export function subtaskProgress(subtasks) {
  const list = arr(subtasks).filter(isObj);
  return { done: list.filter(s => s.status === 'COMPLETED').length, total: list.length };
}

/** store updateTaskSubtasks' mapping (sortOrder = position; falsy completion fields left out). */
function normalizeSubtasks(list, now) {
  return list.map((st, index) => {
    const out = {
      id: st.id,
      content: st.content,
      status: st.status || 'INCOMPLETE',
      sortOrder: index,
      createdAt: st.createdAt || now || null,
    };
    if (st.completedAt) out.completedAt = st.completedAt;
    if (st.completedByEmpID) out.completedByEmpID = st.completedByEmpID;
    return out;
  });
}

/**
 * One subtask change, as TodoDetails' autosave makes it. op:
 *   { type: 'add', content, id }        → appended INCOMPLETE, createdAt = ctx.now (blank content → null)
 *   { type: 'toggle', index }           → COMPLETED (completedAt = ctx.now, completedByEmpID = ctx.userID) ⇄ INCOMPLETE
 *   { type: 'remove', index }
 *   { type: 'move', from, to }          → reorder
 * Returns { id, tags, subtasks } — dispatch tasks/updateTaskSubtasks({ taskId: id, subtasks }).
 * null when the op does nothing (bad index, blank text, unknown type).
 */
export function subtasksPatch(task, op, ctx) {
  const t = isObj(task) ? task : {};
  const c = isObj(ctx) ? ctx : {};
  const o = isObj(op) ? op : {};
  const list = arr(t.subtasks).filter(isObj).map(s => Object.assign({}, s));
  const inRange = i => Number.isInteger(i) && i >= 0 && i < list.length;
  const now = c.now instanceof Date ? new Date(c.now.getTime()) : null;
  switch (o.type) {
    case 'add': {
      const content = typeof o.content === 'string' ? o.content.trim() : '';
      if (!content || !present(o.id)) return null;
      list.push({ id: o.id, content, status: 'INCOMPLETE', sortOrder: list.length, createdAt: now });
      break;
    }
    case 'toggle': {
      if (!inRange(o.index)) return null;
      const s = list[o.index];
      const done = s.status !== 'COMPLETED';
      list[o.index] = Object.assign({}, s, {
        status: done ? 'COMPLETED' : 'INCOMPLETE',
        completedAt: done ? now : null,
        completedByEmpID: done ? c.userID : null,
      });
      break;
    }
    case 'remove':
      if (!inRange(o.index)) return null;
      list.splice(o.index, 1);
      break;
    case 'move': {
      if (!inRange(o.from) || !Number.isInteger(o.to) || o.to < 0 || o.to >= list.length || o.from === o.to) return null;
      const moved = list.splice(o.from, 1)[0];
      list.splice(o.to, 0, moved);
      break;
    }
    default:
      return null;
  }
  return { id: t.id, tags: arr(t.tags).slice(), subtasks: normalizeSubtasks(list, now) };
}

// ---- Attachments (wiki pages) -------------------------------------------------------------------

/** The task's wiki page IDs as TodoDetails reads them (an array, or a single string). */
export function wikiPageIDs(task) {
  const w = isObj(task) ? task.wikiPages : null;
  if (Array.isArray(w)) return w.slice();
  return typeof w === 'string' && w ? [w] : [];
}

/** The wikiPages rows (WikiPageID, Name …) attached to the task — TodoDetails initializeWikiPages. */
export function attachedWikiPages(task, wikiPages) {
  const ids = wikiPageIDs(task).map(String);
  return arr(wikiPages).filter(p => isObj(p) && ids.indexOf(String(p.WikiPageID)) !== -1);
}

/** The Attachments save: wikiPages = the attached WikiPageIDs (written even when [] — difference 2). */
export function wikiPagesPatch(task, ids, ctx) {
  const out = [];
  arr(ids).forEach((id) => {
    if (present(id) && !out.some(x => String(x) === String(id))) out.push(id);
  });
  return Object.assign(basePatch(task, ctx), { wikiPages: out });
}

// ---- Status --------------------------------------------------------------------------------

/** "Reopen": INCOMPLETE with the completion cleared (difference 3). Completing goes through completeTask.js. */
export function reopenPatch(task, ctx) {
  return Object.assign(basePatch(task, ctx), {
    status: 'INCOMPLETE',
    completedAt: null,
    completedAtStr: null,
    completedByEmpID: null,
  });
}

/**
 * Opening the task as someone in its unreadCommentsList takes them out (TodoDetails init). null
 * when there is nothing to change. Only the list (and the always-carried fields) is written.
 */
export function markReadPatch(task, userID) {
  const list = isObj(task) ? task.unreadCommentsList : null;
  if (!Array.isArray(list) || list.indexOf(userID) === -1) return null;
  return Object.assign(basePatch(task, null), { unreadCommentsList: list.filter(a => a !== userID) });
}

// ---- Comments ------------------------------------------------------------------------------

/**
 * TodoDetails onSaveToCommLogChange: ticking "Save to CommLog" is kept only with exactly one tagged
 * patient; otherwise it reverts with a warning. Unticking never warns.
 */
export function commLogTick(checked, taggedCount) {
  if (!checked) return { checked: false, warning: '' };
  if (taggedCount === COMMLOG_PATIENTS) return { checked: true, warning: '' };
  return {
    checked: false,
    warning: taggedCount === 0
      ? "No patient is tagged on this task, so this comment can't be saved to the commlog. Tag a patient first."
      : 'This task has more than one patient tagged. The commlog needs exactly one patient — remove the extra tag first.',
  };
}

/**
 * The tasks/addTaskComment payload — TodoDetails' save, field for field. null for a blank comment.
 * ctx = { text, saveToCommLog (boolean), userID, user ({ Name, DefaultDB, ODUserNum }), now }.
 * The tagged patients are read from the task's content.
 */
export function commentPayload(task, ctx) {
  const t = isObj(task) ? task : {};
  const c = isObj(ctx) ? ctx : {};
  const text = typeof c.text === 'string' ? c.text : '';
  if (!text.trim()) return null; // TodoDetails posts any non-'' text; a blank-only comment is refused here
  const user = isObj(c.user) ? c.user : {};
  const comment = {
    createdAt: c.now instanceof Date ? new Date(c.now.getTime()) : c.now,
    siteID: t.siteID,
    employeeID: c.userID,
    createdByEmpName: user.Name,
    taskID: t.id,
    text,
    saveToCommLog: c.saveToCommLog ? 'TRUE' : 'FALSE',
    taggedPatients: taggedPatientIDs(t.content),
    defaultDB: user.DefaultDB,
    userODNum: user.ODUserNum,
  };
  return {
    comment,
    assignees: arr(t.assignees),
    taskID: t.id,
    taskContent: t.content,
    commenterID: c.userID,
    siteID: t.siteID,
  };
}

// ---- Delete --------------------------------------------------------------------------------

/**
 * ToDo.vue's delete permission (TASKS_WHO_CAN_DELETE, default 'creator'):
 *   whole task    — the creator or a Super Admin, unless the setting is not 'creator';
 *   this instance — the creator only (deleteThisInstanceOnly has no Super Admin exception).
 * `thisOne` is offered for an open repeating task (TodoItem's "delete-repeated" modal).
 */
export function deleteRights(task, ctx) {
  const t = isObj(task) ? task : {};
  const c = isObj(ctx) ? ctx : {};
  const setting = typeof c.whoCanDelete === 'string' && c.whoCanDelete ? c.whoCanDelete.toLowerCase() : 'creator';
  const isCreator = t.createdByEmpID === c.userID;
  const isSuperAdmin = c.role === 'Super Admin';
  const repeating = !!t.repeat && t.status === 'INCOMPLETE';
  return {
    repeating,
    all: !(setting === 'creator' && !isCreator && !isSuperAdmin),
    thisOne: repeating && !(setting === 'creator' && !isCreator),
  };
}

/**
 * tasks/deleteTask payload (ToDo.deleteItem): soft delete — or, for an open repeating task
 * ("All"), permanentDelete: true, as TodoItem.deleteItem flags it. ctx = { userID, now }.
 */
export function deleteTaskPayload(task, ctx) {
  const t = isObj(task) ? task : {};
  const c = isObj(ctx) ? ctx : {};
  return {
    id: t.id,
    deleted: true,
    deletedBy: c.userID,
    deletedOn: c.now instanceof Date ? new Date(c.now.getTime()) : c.now,
    permanentDelete: !!(t.repeat && t.status === 'INCOMPLETE'),
  };
}

/**
 * tasks/deleteThisInstanceOfRepeatedTask payload (TodoItem.deleteThisInstanceOnly + ToDo): the task
 * (comments left out — the action writes the whole object back) with deletedAt, and the office
 * dueAtStr format. ctx = { now, timeZone }.
 */
export function deleteThisOnePayload(task, ctx) {
  const c = isObj(ctx) ? ctx : {};
  const copy = withoutComments(task);
  copy.deletedAt = c.now instanceof Date ? c.now.getTime() : c.now;
  return { task: copy, clientDateFormat: dueAtStrFormat(c.timeZone) };
}

// ---- Header ---------------------------------------------------------------------------------

/** "T-" + the first 6 characters of the Firestore id, upper-cased ('' without an id). */
export function taskIdLabel(id) {
  return present(id) ? `T-${String(id).slice(0, 6).toUpperCase()}` : '';
}

/** The task's deep link: `${origin}/app/todo/<id>`. */
export function taskLink(origin, id) {
  return `${String(origin || '').replace(/\/+$/, '')}/app/todo/${encodeURIComponent(String(id == null ? '' : id))}`;
}
